import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { generateCompleteMockDataset } from '../utils/mockDataGenerator';
import { Lender, Series, NAVHistory, Crystallisation, User } from '../types';

interface MockDataContextType {
  lenders: Lender[];
  series: Series[];
  navHistory: NAVHistory[];
  crystallisations: Crystallisation[];
  users: User[];
  totalPoolNAV: number;
  loading: boolean;
  refreshData: () => void;
}

const MockDataContext = createContext<MockDataContextType | undefined>(undefined);

export const useMockData = () => {
  const context = useContext(MockDataContext);
  if (!context) {
    throw new Error('useMockData must be used within MockDataProvider');
  }
  return context;
};

interface MockDataProviderProps {
  children: ReactNode;
}

export const MockDataProvider: React.FC<MockDataProviderProps> = ({ children }) => {
  const [mockData, setMockData] = useState<ReturnType<typeof generateCompleteMockDataset> | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMockData = () => {
    setLoading(true);
    // Simulate async data loading
    setTimeout(() => {
      const data = generateCompleteMockDataset();
      setMockData(data);
      setLoading(false);
      console.log('Mock data loaded:', data.summary);
    }, 500);
  };

  useEffect(() => {
    loadMockData();
  }, []);

  const value: MockDataContextType = {
    lenders: mockData?.lenders || [],
    series: mockData?.series || [],
    navHistory: mockData?.navHistory || [],
    crystallisations: mockData?.crystallisations || [],
    users: mockData?.users || [],
    totalPoolNAV: mockData?.totalPoolNAV || 0,
    loading,
    refreshData: loadMockData,
  };

  return (
    <MockDataContext.Provider value={value}>
      {children}
    </MockDataContext.Provider>
  );
};
